// src/api.js
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "https://citalumniblr.org";

export const api = axios.create({
  baseURL: BASE_URL.replace(/\/$/, ""),
  headers: { "Content-Type": "application/json" },
  timeout: 30000
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = "Bearer " + token;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const msg = (error.response.data && error.response.data.message) || "";
      if (msg.indexOf("expired") !== -1 || msg.indexOf("Invalid") !== -1) {
        localStorage.removeItem("token");
        localStorage.removeItem("loggedInUser");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

// ─── localStorage helpers ───────────────────────────────
export const saveAlumniRecord = (record) => {
  const existing = getAllAlumniRecords();
  existing.push(record);
  localStorage.setItem("alumniRecords", JSON.stringify(existing));
};

export const getAllAlumniRecords = () => {
  const r = localStorage.getItem("alumniRecords");
  return r ? JSON.parse(r) : [];
};

export const findAlumniByEmail = (email) => {
  return getAllAlumniRecords().find((r) => r.email === email);
};

export const updateAlumniStatus = (id, newStatus, extra) => {
  const records = getAllAlumniRecords();
  const updated = records.map((r) =>
    r.id === id ? { ...r, status: newStatus, ...extra, updatedAt: new Date().toISOString() } : r
  );
  localStorage.setItem("alumniRecords", JSON.stringify(updated));
  return updated.find((r) => r.id === id);
};

export const getApprovedAlumni = () =>
  getAllAlumniRecords().filter((r) => r.status === "Approved");

export const getPendingAlumni = () =>
  getAllAlumniRecords().filter((r) => r.status === "Pending" || r.status === "Under Verification");

export const generateAlumniId = () => {
  const approved = getAllAlumniRecords().filter((r) => r.alumniId && r.alumniId.startsWith("ADLINK-"));
  let max = 0;
  approved.forEach((r) => {
    const n = parseInt(r.alumniId.replace("ADLINK-", ""), 10);
    if (!isNaN(n) && n > max) max = n;
  });
  return "ADLINK-" + String(max + 1).padStart(5, "0");
};

export const markPaymentLinkSent = (id) => {
  const records = getAllAlumniRecords();
  const updated = records.map((r) =>
    r.id === id ? { ...r, paymentLinkSent: true, paymentLinkSentAt: new Date().toISOString() } : r
  );
  localStorage.setItem("alumniRecords", JSON.stringify(updated));
  return updated.find((r) => r.id === id);
};

export const saveDonation = (d) => {
  const list = JSON.parse(localStorage.getItem("donations") || "[]");
  list.push({ ...d, status: "Success" });
  localStorage.setItem("donations", JSON.stringify(list));
  return d;
};

export const getAllDonations = () =>
  JSON.parse(localStorage.getItem("donations") || "[]");

export const rejectAlumniApplication = (id) => updateAlumniStatus(id, "Rejected");

// ─── API calls ──────────────────────────────────────────
export const getCurrentUserProfile = async () => {
  try {
    const res = await api.get("/api/auth/me");
    return { success: true, user: res.data.user };
  } catch (err) {
    return { success: false, message: (err.response && err.response.data && err.response.data.message) || "Failed to fetch profile" };
  }
};

export const updateUserProfile = async (data) => {
  try {
    const res = await api.put("/api/auth/profile", data);
    return { success: true, user: res.data.user, message: res.data.message || "Profile updated successfully" };
  } catch (err) {
    return { success: false, message: (err.response && err.response.data && err.response.data.message) || "Failed to update profile" };
  }
};

export const registerUser = async (userData) => {
  try {
    const res = await api.post("/api/auth/register", userData);
    return { success: true, token: res.data.token, user: res.data.user, message: res.data.message };
  } catch (err) {
    return { success: false, message: (err.response && err.response.data && err.response.data.message) || "Registration failed" };
  }
};

export const loginUser = async (email, password) => {
  try {
    const res = await api.post("/api/auth/login", { email, password });
    return { success: true, token: res.data.token, user: res.data.user };
  } catch (err) {
    return { success: false, message: (err.response && err.response.data && err.response.data.message) || "Login failed" };
  }
};

export const fetchApprovedAlumni = async () => {
  try {
    const res = await api.get("/api/auth/approved");
    return { success: true, alumni: res.data };
  } catch (err) {
    return { success: false, message: "Failed to fetch alumni list", alumni: [] };
  }
};